/**
 * AuthOverrideStep
 *
 * Shown when the OpenAPI spec only declares OAuth2 / OpenIdConnect (or no auth
 * at all) but the API still requires some kind of static credential the user
 * can paste. Most cloud APIs that use OAuth in their official spec also offer
 * a personal access token (Atlassian, GitHub, GitLab, Linear, Notion, etc.),
 * so we let the user pick the override mode and then fall through to the
 * normal AuthConfigStep inputs by mapping the selection to a synthetic
 * AuthScheme.
 *
 * UX: three small radio cards (Bearer / Basic / API key) + a "no auth needed"
 * escape hatch. Picking nothing keeps Generate disabled.
 */

import { motion } from "framer-motion";
import { KeyRound, AlertTriangle } from "lucide-react";
import type { AuthScheme } from "./AuthConfigStep";

export type OverrideMode = "none" | "bearer" | "basic" | "apiKey";

type Props = {
  /** Why we are showing the override (drives the explanatory copy). */
  reason: "oauth2" | "missing";
  /** Currently selected override mode (controlled). */
  value: OverrideMode | null;
  onChange: (mode: OverrideMode) => void;
  /** Render inside a parent card (no outer glass wrapper). */
  embedded?: boolean;
  /** Separator when stacked below another setup block in the same card. */
  withTopDivider?: boolean;
};

/** Translate the user's override choice into a synthetic AuthScheme so the
 *  downstream AuthConfigStep can render the matching input fields. */
export function overrideToScheme(mode: OverrideMode | null): AuthScheme[] {
  if (!mode || mode === "none") return [];
  if (mode === "bearer") return [{ type: "http", scheme: "bearer" }];
  if (mode === "basic")  return [{ type: "http", scheme: "basic" }];
  if (mode === "apiKey") return [{ type: "apiKey", name: "X-API-Key", location: "header" }];
  return [];
}

const OPTIONS: Array<{
  mode: OverrideMode;
  title: string;
  subtitle: string;
}> = [
  {
    mode: "bearer",
    title: "Bearer token",
    subtitle: "One API token / secret key (OpenAI, Stripe, GitHub PAT)",
  },
  {
    mode: "basic",
    title: "Basic (email + token)",
    subtitle: "Email or username plus an API token or password",
  },
  {
    mode: "apiKey",
    title: "API key in header",
    subtitle: "A single key sent as a request header",
  },
  {
    mode: "none",
    title: "No authentication",
    subtitle: "The API is fully public",
  },
];

function AuthOverrideFields({ reason, value, onChange }: Pick<Props, "reason" | "value" | "onChange">) {
  const headline =
    reason === "oauth2"
      ? "This API uses OAuth — pick a credential to use instead"
      : "We couldn't detect the API's auth — pick one";

  const sub =
    reason === "oauth2"
      ? "Hosted MCPs can't run interactive OAuth flows yet. Most APIs that use OAuth also accept a personal access token — choose how you'd like to authenticate."
      : "The spec didn't declare a security scheme. If the API needs credentials, choose the closest match below.";

  return (
    <>
          {/* Header */}
          <div className="flex items-start gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted flex-shrink-0">
              {reason === "oauth2" ? (
                <AlertTriangle className="w-4 h-4 text-muted-foreground" />
              ) : (
                <KeyRound className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground leading-tight">
                {headline}
              </h3>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {sub}
              </p>
            </div>
          </div>

          {/* Radio grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {OPTIONS.map(opt => {
              const selected = value === opt.mode;
              return (
                <button
                  key={opt.mode}
                  type="button"
                  onClick={() => onChange(opt.mode)}
                  className={[
                    "text-left rounded-xl border px-3.5 py-3 transition",
                    "focus:outline-none focus:ring-2 focus:ring-ring/20",
                    selected
                      ? "border-foreground/40 bg-background/80 shadow-soft"
                      : "border-border bg-muted/30 hover:bg-muted/50",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "w-3.5 h-3.5 rounded-full border flex-shrink-0",
                        selected
                          ? "border-foreground bg-foreground"
                          : "border-border",
                      ].join(" ")}
                    />
                    <span className="text-sm font-medium text-foreground">
                      {opt.title}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug pl-5">
                    {opt.subtitle}
                  </p>
                </button>
              );
            })}
          </div>
    </>
  );
}

export default function AuthOverrideStep({
  reason,
  value,
  onChange,
  embedded = false,
  withTopDivider = false,
}: Props) {
  const fields = <AuthOverrideFields reason={reason} value={value} onChange={onChange} />;

  if (embedded) {
    return (
      <div className={withTopDivider ? "pt-6 border-t border-border" : undefined}>{fields}</div>
    );
  }

  return (
    <motion.section
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="py-0 px-6"
    >
      <div className="container max-w-2xl mx-auto">
        <div className="glass-strong rounded-2xl p-7 shadow-soft">{fields}</div>
      </div>
    </motion.section>
  );
}
